import React, { useRef, useEffect, useState } from "react";
import styled from "styled-components";
import { palette } from "@/styles/palette";

// 색상 변수 정의 - palette 사용
const COLORS = {
    primary: "#007F73", // 프로젝트 기본 색상 유지
    secondary: "#33A38E",
    tertiary: "#77C3A6",
    quaternary: "#B2D9B8",
    quinary: "#CDEDD4",
    text: {
        primary: palette.black,
        secondary: "#464A4D",
        tertiary: "#767676"
    }
};

interface FlavorChartProps {
    data: {
        label: string;
        value: number;
    }[];
}

const FlavorChart: React.FC<FlavorChartProps> = ({ data }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const animationRef = useRef<number>();
    const [isFirstRender, setIsFirstRender] = useState(true);
    const total = data.reduce((acc, curr) => acc + curr.value, 0);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        let animationProgress = 0;
        const animationDuration = 1000; // 1초
        const startTime = performance.now();

        const updateCanvas = (currentTime: number = 0) => {
            // 컨테이너의 실제 너비 가져오기
            const container = canvas.parentElement;
            if (!container) return;

            const containerWidth = container.clientWidth;
            const scale = Math.min(1, containerWidth / 319);

            // 캔버스 크기 설정
            canvas.width = 319 * scale;
            canvas.height = 256 * scale;

            // 중심점 및 반지름 설정
            const centerX = canvas.width / 2;
            const centerY = canvas.height / 2;
            const radius = 70 * scale;

            // 애니메이션 진행률 계산
            if (isFirstRender) {
                const elapsed = currentTime - startTime;
                animationProgress = Math.min(elapsed / animationDuration, 1);
            } else {
                animationProgress = 1;
            }

            // 파이 차트 그리기
            let startAngle = -Math.PI / 2;

            const colors = [COLORS.primary, COLORS.secondary, COLORS.tertiary, COLORS.quaternary, COLORS.quinary];

            // 배경 지우기
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            data.forEach((item, index) => {
                const sliceAngle = (2 * Math.PI * item.value) / total;
                // 애니메이션 적용
                const currentSliceAngle = sliceAngle * animationProgress;
                const endAngle = startAngle + currentSliceAngle;
                const midAngle = startAngle + currentSliceAngle / 2;

                // 파이 조각 그리기
                ctx.beginPath();
                ctx.moveTo(centerX, centerY);
                ctx.arc(centerX, centerY, radius, startAngle, endAngle);
                ctx.fillStyle = colors[index];
                ctx.fill();

                // 레이블과 선은 애니메이션이 80% 이상 진행됐을 때만 표시
                if (animationProgress > 0.8) {
                    const opacity = (animationProgress - 0.8) * 5; // 0.8에서 1.0까지 투명도 증가

                    const lineLength = 20 * scale;
                    const horizontalLength = 10 * scale;

                    const lineStart = {
                        x: centerX + Math.cos(midAngle) * radius,
                        y: centerY + Math.sin(midAngle) * radius
                    };

                    const lineEnd = {
                        x: centerX + Math.cos(midAngle) * (radius + lineLength),
                        y: centerY + Math.sin(midAngle) * (radius + lineLength)
                    };

                    // 선 그리기
                    ctx.beginPath();
                    ctx.moveTo(lineStart.x, lineStart.y);
                    ctx.lineTo(lineEnd.x, lineEnd.y);

                    const direction = midAngle > Math.PI / 2 && midAngle < (3 * Math.PI) / 2 ? -1 : 1;
                    ctx.lineTo(lineEnd.x + horizontalLength * direction, lineEnd.y);

                    ctx.strokeStyle = `rgba(209, 209, 209, ${opacity})`;
                    ctx.lineWidth = 1;
                    ctx.stroke();

                    // 텍스트 그리기
                    const labelX = lineEnd.x + (horizontalLength + 5 * scale) * direction;
                    const labelY = lineEnd.y;

                    ctx.font = `600 ${16 * scale}px Pretendard`;
                    ctx.fillStyle = `rgba(${index === 0 ? "0, 127, 115" : "51, 163, 142"}, ${opacity})`;
                    ctx.textAlign = direction > 0 ? "left" : "right";
                    ctx.textBaseline = "bottom";
                    ctx.fillText(item.label, labelX, labelY);

                    ctx.font = `400 ${14 * scale}px Pretendard`;
                    ctx.fillStyle = `rgba(0, 0, 0, ${opacity})`;
                    ctx.textBaseline = "top";
                    ctx.fillText(`${item.value.toLocaleString()}명`, labelX, labelY + 3 * scale);
                }

                startAngle = startAngle + sliceAngle;
            });

            // 애니메이션이 진행 중이면 다음 프레임 요청
            if (isFirstRender && animationProgress < 1) {
                animationRef.current = requestAnimationFrame(updateCanvas);
            } else if (isFirstRender) {
                setIsFirstRender(false);
            }
        };

        // 초기 렌더링
        if (isFirstRender) {
            animationRef.current = requestAnimationFrame(updateCanvas);
        } else {
            updateCanvas();
        }

        // 리사이즈 이벤트 리스너
        const handleResize = () => {
            updateCanvas();
        };
        window.addEventListener("resize", handleResize);

        // 클린업
        return () => {
            window.removeEventListener("resize", handleResize);
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [data, isFirstRender]);

    return (
        <ChartContainer>
            <Canvas ref={canvasRef} />
        </ChartContainer>
    );
};

export default FlavorChart;

const ChartContainer = styled.div`
    width: 100%;
    max-width: 319px;
    aspect-ratio: 319 / 256;
    position: relative;
    margin: 0 auto;
`;

const Canvas = styled.canvas`
    width: 100%;
    height: 100%;
    display: block;
`;
